import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  COLUMN_TYPES, type CellStyle, type Column, type ColumnType,
  type ConditionalRule, type Merge, type Rec,
} from '../lib/api';
import { COLUMN_TYPE_LABELS } from '../lib/ui';
import type { Computed } from '../lib/formula/engine';
import { matchRule } from '../lib/conditional';
import { useContextMenu, type MenuEntry } from './ContextMenu';
import { ColumnFilter } from './ColumnFilter';
import { ColumnTypeIcon } from './ColumnTypeIcon';
import {
  ArrowDownWideNarrow, ArrowLeft, ArrowRight, ArrowRightLeft, Check, ClipboardPaste, Copy, Eraser,
  Eye, EyeOff, History, Pencil, Plus, Scissors, SlidersHorizontal, Trash2, Type, X,
} from 'lucide-react';
import { ERROR_HINTS, isFormula, shiftFormulaReferences } from '../lib/formula/engine';
import { useToast } from './Toast';

export function isCheckboxChecked(value: unknown): boolean {
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === 'yes' || v === 'да' || v === 'истина' || v === '✓' || v === '1';
  }
  return false;
}
import {
  COL_W, GUTTER_W, R, ROW_H, colLabel, ft, mono, tdCell, tdGutter, thCell, thCorner,
} from '../lib/ui';

export interface Sel {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

export function reorderColumnList(
  columns: Column[],
  sourceColId: string,
  targetColId: string,
  side: 'left' | 'right' = 'left',
): string[] {
  const currentIds = columns.map((c) => c.fields.colId);
  const sourceIdx = currentIds.indexOf(sourceColId);
  const targetIdx = currentIds.indexOf(targetColId);
  if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) {
    return currentIds;
  }
  const remaining = currentIds.filter((id) => id !== sourceColId);
  let newTargetIdx = remaining.indexOf(targetColId);
  if (side === 'right') {
    newTargetIdx += 1;
  }
  remaining.splice(newTargetIdx, 0, sourceColId);
  return remaining;
}

export function moveColumnByDirection(
  columns: Column[],
  colId: string,
  dir: 'left' | 'right',
): string[] {
  const currentIds = columns.map((c) => c.fields.colId);
  const idx = currentIds.indexOf(colId);
  if (idx === -1) return currentIds;
  const targetIdx = dir === 'left' ? idx - 1 : idx + 1;
  if (targetIdx < 0 || targetIdx >= currentIds.length) return currentIds;
  const newIds = [...currentIds];
  const [removed] = newIds.splice(idx, 1);
  newIds.splice(targetIdx, 0, removed);
  return newIds;
}

interface Props {
  columns: Column[];
  records: Rec[];
  editable: boolean;
  onPatch: (records: Rec[]) => void;
  onCreate: (records: { fields: Record<string, unknown> }[]) => void;
  onSelection?: (sel: Sel | null) => void;
  onOpenHistory?: (recordId: string) => void;
  onOpenColumn?: (column: Column) => void;
  computed?: Map<string, Computed>;
  styles?: Map<string, CellStyle>;
  merges?: Merge[];
  conditional?: ConditionalRule[];
  colWidths?: Map<string, number>;
  rowHeights?: Map<string, number>;
  onResizeColumn?: (colId: string, width: number) => void;
  onResizeRow?: (recordId: string, height: number) => void;
  sort?: { colId: string; dir: 'asc' | 'desc' } | null;
  onSort?: (sort: { colId: string; dir: 'asc' | 'desc' } | null) => void;
  /** Активные фильтры и наборы значений для выпадашки в шапке. */
  filters?: Map<string, Set<string>>;
  valuesFor?: (colId: string) => string[];
  onFilter?: (colId: string, filter: Set<string> | null) => void;
  /** Операции из контекстных меню — данные меняет вызывающий. */
  onAddRow?: (count?: number, afterId?: string) => void;
  onDeleteRows?: (ids: string[]) => void;
  onAddColumn?: () => void;
  onRenameColumn?: (column: Column) => void;
  onDeleteColumn?: (column: Column) => void;
  /** Сброс размера к автоматическому. */
  onAutoWidth?: (colId: string) => void;
  onAutoHeight?: (recordId: string) => void;
  onSetColumnType?: (column: Column, type: ColumnType) => void;
  onInsertImage?: () => void;
  hiddenColIds?: Set<string>;
  onToggleHideColumn?: (colId: string) => void;
  onUnhideAllColumns?: () => void;
  onUnhideColumns?: (colIds: string[]) => void;
  onReorderColumns?: (colIds: string[]) => void;
  onMoveColumn?: (col: Column, dir: 'left' | 'right') => void;
  onInsertColumnAt?: (pos: number) => void;
}

const norm = (a: { r: number; c: number }, b: { r: number; c: number }): Sel => ({
  r1: Math.min(a.r, b.r),
  r2: Math.max(a.r, b.r),
  c1: Math.min(a.c, b.c),
  c2: Math.max(a.c, b.c),
});

function display(value: unknown, type: Column['fields']['type'], label?: string): string {
  if (value === null || value === undefined || value === '') return '';
  if (type === 'Bool') return isCheckboxChecked(value) ? 'TRUE' : 'FALSE';
  const isInt = type === 'Int' || (label ? /преми/i.test(label) : false);
  if (isInt) {
    const n = Number(typeof value === 'string' ? value.replace(/\s+/g, '').replace(',', '.') : value);
    return Number.isFinite(n)
      ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(n))
      : String(value);
  }
  if (type === 'Numeric') {
    const n = Number(typeof value === 'string' ? value.replace(/\s+/g, '').replace(',', '.') : value);
    return Number.isFinite(n) ? new Intl.NumberFormat('ru-RU').format(n) : String(value);
  }
  if (type === 'Date') {
    const d = new Date(String(value));
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('ru-RU');
  }
  if (type === 'DateTime') {
    const d = new Date(String(value));
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString('ru-RU');
  }
  return String(value);
}

export { colLabel };

export function Grid({
  columns, records, editable, onPatch, onCreate, onSelection, onOpenHistory, onOpenColumn,
  computed, styles, merges, conditional, colWidths, rowHeights,
  onResizeColumn, onResizeRow, sort, onSort,
  onAddRow, onDeleteRows, onAddColumn, onRenameColumn, onDeleteColumn,
  onAutoWidth, onAutoHeight, filters, valuesFor, onFilter,
  onSetColumnType, onInsertImage,
  hiddenColIds, onToggleHideColumn, onUnhideAllColumns, onUnhideColumns,
  onReorderColumns, onMoveColumn, onInsertColumnAt,
}: Props) {
  const toast = useToast();
  const menu = useContextMenu();
  const [anchor, setAnchor] = useState({ r: 0, c: 0 });
  const [head, setHead] = useState({ r: 0, c: 0 });
  const [editing, setEditing] = useState<{ r: number; c: number; initial: string } | null>(null);
  const [copied, setCopied] = useState<Sel | null>(null);
  const [fillRange, setFillRange] = useState<Sel | null>(null);
  const fillSourceRef = useRef<Sel | null>(null);
  const fillTargetRowRef = useRef<number | null>(null);
  const [viewport, setViewport] = useState({ top: 0, height: 0 });

  const [dragColId, setDragColId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ colId: string; side: 'left' | 'right' } | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, colId: string) => {
    if (!editable) return;
    setDragColId(colId);
    e.dataTransfer.setData('text/plain', colId);
    e.dataTransfer.effectAllowed = 'move';
  }, [editable]);

  const handleDragOver = useCallback((e: React.DragEvent, colId: string) => {
    if (!editable || !dragColId || dragColId === colId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const isRight = (e.clientX - rect.left) > rect.width / 2;
    const side = isRight ? 'right' : 'left';
    setDropTarget((prev) => (prev?.colId === colId && prev?.side === side ? prev : { colId, side }));
  }, [editable, dragColId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropTarget(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetColId: string) => {
    e.preventDefault();
    if (!editable || !dragColId || dragColId === targetColId) {
      setDragColId(null);
      setDropTarget(null);
      return;
    }
    const side = dropTarget?.side ?? 'left';
    const sourceId = dragColId;
    setDragColId(null);
    setDropTarget(null);
    if (onReorderColumns) {
      const newOrder = reorderColumnList(columns, sourceId, targetColId, side);
      onReorderColumns(newOrder);
    }
  }, [editable, dragColId, dropTarget, onReorderColumns, columns]);

  const handleDragEnd = useCallback(() => {
    setDragColId(null);
    setDropTarget(null);
  }, []);

  const dragging = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const rafHeadId = useRef<number | null>(null);
  const pendingHead = useRef<{ r: number; c: number } | null>(null);
  const parentRafId = useRef<number | null>(null);

  const visibleCols = useMemo(() => {
    const list = columns
      .map((col, originalIndex) => ({ col, originalIndex }))
      .filter(({ col }) => !hiddenColIds?.has(col.fields.colId));
    return list.length > 0 ? list : columns.map((col, originalIndex) => ({ col, originalIndex }));
  }, [columns, hiddenColIds]);

  const visibleColumns = useMemo(() => visibleCols.map((x) => x.col), [visibleCols]);

  const toggleCheckbox = useCallback(
    (rec: Rec, col: Column) => {
      if (!editable || col.fields.isFormula) return;
      const current = rec.fields[col.fields.colId];
      if (isFormula(current)) return;
      const nextVal = !isCheckboxChecked(current);
      onPatch([{ id: rec.id, fields: { [col.fields.colId]: nextVal } }]);
    },
    [editable, onPatch],
  );

  const sel = useMemo(() => norm(anchor, head), [anchor, head]);

  useEffect(() => {
    if (!onSelection) return;
    if (dragging.current) {
      if (!parentRafId.current) {
        parentRafId.current = requestAnimationFrame(() => {
          parentRafId.current = null;
          onSelection(sel);
        });
      }
    } else {
      onSelection(sel);
    }
    return () => {
      if (parentRafId.current) {
        cancelAnimationFrame(parentRafId.current);
        parentRafId.current = null;
      }
    };
  }, [sel, onSelection]);

  useEffect(() => {
    const stop = () => {
      if (dragging.current) {
        dragging.current = false;
        if (rafHeadId.current) {
          cancelAnimationFrame(rafHeadId.current);
          rafHeadId.current = null;
        }
        if (pendingHead.current) {
          const next = pendingHead.current;
          pendingHead.current = null;
          setHead(next);
        }
      }
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const widthOf = useCallback((colId: string) => colWidths?.get(colId) ?? COL_W, [colWidths]);
  const heightOf = useCallback((recordId: string) => rowHeights?.get(recordId) ?? ROW_H, [rowHeights]);

  /**
   * Объединения привязаны к идентификаторам строки и колонки, а не к
   * координатам: при сортировке и вставке строк координаты разъезжаются.
   * Индексы считаем здесь, на клиенте.
   */
  const { origins, skipped } = useMemo(() => {
    const rowIndex = new Map(records.map((rec, i) => [rec.id, i]));
    const colIndex = new Map(visibleCols.map(({ col }, i) => [col.fields.colId, i]));

    const origins = new Map<string, { rowspan: number; colspan: number }>();
    const skipped = new Set<string>();

    for (const m of merges ?? []) {
      const r = rowIndex.get(m.topRecordId);
      const c = colIndex.get(m.leftColId);
      if (r === undefined || c === undefined) continue;

      origins.set(`${r}:${c}`, { rowspan: m.rowspan, colspan: m.colspan });
      for (let rr: number = r; rr < r + m.rowspan; rr++) {
        for (let cc: number = c; cc < c + m.colspan; cc++) {
          if (rr !== r || cc !== c) skipped.add(`${rr}:${cc}`);
        }
      }
    }
    return { origins, skipped };
  }, [merges, records, columns]);

  // Индексируем правила условного форматирования по колонкам для мгновенного доступа без перебора O(M*N)
  const rulesByCol = useMemo(() => {
    if (!conditional?.length) return null;
    const map = new Map<string, ConditionalRule[]>();
    for (const rule of conditional) {
      const key = rule.colId ?? '__all__';
      let arr = map.get(key);
      if (!arr) {
        arr = [];
        map.set(key, arr);
      }
      arr.push(rule);
    }
    return map;
  }, [conditional]);

  // Кэш значений и стилей ячеек: обновляется ТОЛЬКО при реальном изменении данных.
  // При выделении и перемещении мыши ячейки не пересчитываются повторно (0мс задержка).
  const cellCache = useMemo(() => {
    const map = new Map<
      string,
      {
        shown: string;
        style: CellStyle;
        numeric: boolean;
        isError: boolean;
        title?: string;
        isBool?: boolean;
        boolChecked?: boolean;
      }
    >();

    for (let i = 0; i < records.length; i++) {
      const rec = records[i];
      for (let j = 0; j < columns.length; j++) {
        const col = columns[j];
        const stored = rec.fields[col.fields.colId];
        const calc = isFormula(stored)
          ? computed?.get(`${rec.id}:${col.fields.colId}`)
          : undefined;
        const value = calc ? calc.value : stored;
        const isBool = col.fields.type === 'Bool';
        const boolChecked = isBool ? isCheckboxChecked(value) : false;
        const isIntCol = col.fields.type === 'Int' || /преми/i.test(col.fields.label || '');
        const shown = calc
          ? (isIntCol && typeof calc.value === 'number'
              ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(calc.value))
              : calc.formatted)
          : display(value, col.fields.type, col.fields.label);

        let auto: ConditionalRule | undefined;
        if (rulesByCol) {
          const colRules = rulesByCol.get(col.fields.colId);
          if (colRules) auto = colRules.find((rule) => matchRule(rule, value));
          if (!auto) {
            const allRules = rulesByCol.get('__all__');
            if (allRules) auto = allRules.find((rule) => matchRule(rule, value));
          }
        }
        const own = styles?.get(`${rec.id}:${col.fields.colId}`);
        const style: CellStyle = { ...(auto?.style ?? {}), ...(own ?? {}) };
        const numeric =
          col.fields.type === 'Numeric' ||
          col.fields.type === 'Int' ||
          isIntCol ||
          (calc ? typeof calc.value === 'number' : false);
        const isError = Boolean(calc?.isError);
        const title =
          calc?.isError && calc.errorCode
            ? `${calc.formatted} — ${ERROR_HINTS[calc.errorCode] ?? ''}`
            : undefined;

        map.set(`${rec.id}:${col.fields.colId}`, {
          shown,
          style,
          numeric,
          isError,
          title,
          isBool,
          boolChecked,
        });
      }
    }
    return map;
  }, [records, columns, computed, styles, rulesByCol]);

  // Виртуализация со 40+ строк (вместо 200), уменьшает нагрузку на DOM и память в разы
  const virtual = records.length > 40 && origins.size === 0;

  const offsets = useMemo(() => {
    const acc = [0];
    for (const rec of records) acc.push(acc[acc.length - 1] + heightOf(rec.id));
    return acc;
  }, [records, rowHeights]);

  const slice = useMemo(() => {
    if (!virtual) return { from: 0, to: records.length, padTop: 0, padBottom: 0 };

    const find = (y: number) => {
      let lo = 0;
      let hi = records.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (offsets[mid + 1] <= y) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };

    const from = Math.max(0, find(viewport.top) - 12);
    const to = Math.min(records.length, find(viewport.top + viewport.height) + 12);
    return { from, to, padTop: offsets[from], padBottom: offsets[records.length] - offsets[to] };
  }, [virtual, viewport, offsets, records.length]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !virtual) return;

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (el) setViewport({ top: el.scrollTop, height: el.clientHeight });
          ticking = false;
        });
        ticking = true;
      }
    };

    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });

    const observer = new ResizeObserver(() => {
      if (el) setViewport({ top: el.scrollTop, height: el.clientHeight });
    });
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [virtual]);

  const inSel = (s: Sel, r: number, c: number) =>
    r >= s.r1 && r <= s.r2 && c >= s.c1 && c <= s.c2;

  const move = useCallback(
    (dr: number, dc: number, extend: boolean) => {
      const r = Math.max(0, Math.min(records.length - 1, head.r + dr));
      const c = Math.max(0, Math.min(visibleCols.length - 1, head.c + dc));
      setHead({ r, c });
      if (!extend) setAnchor({ r, c });
    },
    [head, records.length, visibleCols.length],
  );

  const startEdit = useCallback(
    (cell: { r: number; c: number }, initial?: string) => {
      const col = visibleCols[cell.c]?.col;
      if (!editable || !col) return;
      if (col.fields.type === 'Bool' && (!initial || initial !== '=')) {
        const rec = records[cell.r];
        if (rec && !col.fields.isFormula && !isFormula(rec.fields[col.fields.colId])) {
          toggleCheckbox(rec, col);
        }
        return;
      }
      const raw = records[cell.r]?.fields[col.fields.colId];
      const initialVal = initial ?? (raw === null || raw === undefined ? '' : String(raw));
      setEditing({ r: cell.r, c: cell.c, initial: initialVal });
    },
    [visibleCols, records, editable, toggleCheckbox],
  );

  const commitEdit = useCallback(
    (value: string, moveDir?: { dr: number; dc: number }) => {
      if (!editing) return;
      const { r, c } = editing;
      const col = visibleCols[c]?.col;
      const rec = records[r];
      setEditing(null);
      if (!col || !rec) return;

      const trimmed = value.trim();
      let nextValue: unknown = value;
      if (col.fields.type === 'Bool' && !trimmed.startsWith('=')) {
        nextValue = isCheckboxChecked(value);
      }

      if (rec.fields[col.fields.colId] !== nextValue) {
        onPatch([{ id: rec.id, fields: { [col.fields.colId]: nextValue } }]);
      }

      if (moveDir) {
        const nextR = Math.max(0, Math.min(records.length - 1, r + moveDir.dr));
        const nextC = Math.max(0, Math.min(visibleCols.length - 1, c + moveDir.dc));
        setAnchor({ r: nextR, c: nextC });
        setHead({ r: nextR, c: nextC });
      }
    },
    [editing, visibleCols, records, onPatch],
  );

  const cancelEdit = useCallback(() => {
    setEditing(null);
  }, []);

  const showCopyToast = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        toast('Скопировано (пустая ячейка)', 'info');
        return;
      }
      const preview = trimmed.length > 35 ? trimmed.slice(0, 35) + '…' : trimmed;
      toast(`Скопировано: «${preview}»`, 'ok');
    },
    [toast],
  );

  const getSelectionText = useCallback((): string => {
    if (!records.length || !visibleCols.length) return '';
    const isSingleCell = sel.r1 === sel.r2 && sel.c1 === sel.c2;
    return records
      .slice(sel.r1, sel.r2 + 1)
      .map((rec) =>
        visibleCols
          .slice(sel.c1, sel.c2 + 1)
          .map(({ col }) => {
            if (col.fields.type === 'Bool') {
              const cached = cellCache.get(`${rec.id}:${col.fields.colId}`);
              return cached?.boolChecked ? 'TRUE' : 'FALSE';
            }
            const isIntCol = col.fields.type === 'Int' || /преми/i.test(col.fields.label || '');
            if (isSingleCell && isIntCol) {
              const calc = isFormula(rec.fields[col.fields.colId])
                ? computed?.get(`${rec.id}:${col.fields.colId}`)
                : undefined;
              const val = calc ? calc.value : rec.fields[col.fields.colId];
              const n = Number(typeof val === 'string' ? val.replace(/\s+/g, '').replace(',', '.') : val);
              if (Number.isFinite(n)) {
                return String(Math.round(n));
              }
            }
            const cached = cellCache.get(`${rec.id}:${col.fields.colId}`);
            const v = cached?.shown ?? rec.fields[col.fields.colId];
            return v === null || v === undefined ? '' : String(v);
          })
          .join('\t'),
      )
      .join('\n');
  }, [records, visibleCols, sel, cellCache, computed]);

  const copySelection = useCallback(
    (clipboardData?: DataTransfer | null) => {
      const tsv = getSelectionText();
      if (clipboardData) {
        try {
          clipboardData.setData('text/plain', tsv);
        } catch {}
      } else if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(tsv).catch(() => {
          const ta = document.createElement('textarea');
          ta.value = tsv;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(sel);
      showCopyToast(tsv);
      setTimeout(() => {
        setCopied((prev) => (prev === sel ? null : prev));
      }, 2500);
    },
    [getSelectionText, sel, showCopyToast],
  );

  const copySelectionPiped = useCallback(() => {
    const txt = records
      .slice(sel.r1, sel.r2 + 1)
      .map((rec) =>
        visibleCols
          .slice(sel.c1, sel.c2 + 1)
          .map(({ col }) => {
            if (col.fields.type === 'Bool') {
              const cached = cellCache.get(`${rec.id}:${col.fields.colId}`);
              return cached?.boolChecked ? 'TRUE' : 'FALSE';
            }
            const cached = cellCache.get(`${rec.id}:${col.fields.colId}`);
            const v = cached?.shown ?? rec.fields[col.fields.colId];
            return v === null || v === undefined ? '' : String(v);
          })
          .join(' | '),
      )
      .join('\n');

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(txt).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = txt;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = txt;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(sel);
    showCopyToast(txt);
    setTimeout(() => {
      setCopied((prev) => (prev === sel ? null : prev));
    }, 2500);
  }, [records, visibleCols, sel, cellCache, showCopyToast]);

  const paste = useCallback(
    (text: string) => {
      if (!editable) return;
      const matrix = text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n')
        .map((line) => line.split('\t'));

      const patches: Rec[] = [];
      const creates: { fields: Record<string, unknown> }[] = [];

      matrix.forEach((line, dr) => {
        const rec = records[sel.r1 + dr];
        const fields: Record<string, unknown> = {};

        line.forEach((value, dc) => {
          const col = visibleCols[sel.c1 + dc]?.col;
          if (col) {
            if (col.fields.type === 'Bool' && !value.trim().startsWith('=')) {
              fields[col.fields.colId] = isCheckboxChecked(value);
            } else {
              fields[col.fields.colId] = value;
            }
          }
        });

        if (!Object.keys(fields).length) return;
        if (rec) patches.push({ id: rec.id, fields });
        else creates.push({ fields });
      });

      if (patches.length) onPatch(patches);
      if (creates.length) onCreate(creates);
    },
    [records, visibleCols, sel, onPatch, onCreate, editable],
  );

  const clear = useCallback(() => {
    if (!editable) return;
    const patches = records.slice(sel.r1, sel.r2 + 1).map((rec) => {
      const fields: Record<string, unknown> = {};
      visibleCols.slice(sel.c1, sel.c2 + 1).forEach(({ col }) => {
        fields[col.fields.colId] = col.fields.type === 'Bool' ? false : null;
      });
      return { id: rec.id, fields };
    });
    if (patches.length) onPatch(patches);
  }, [records, visibleCols, sel, onPatch, editable]);

  const cut = useCallback(() => {
    copySelection();
    clear();
  }, [copySelection, clear]);

  /**
   * Размножает значения/формулы из sourceSel до строки targetR (вниз или вверх).
   * Если в ячейке формула — смещает в ней относительные ссылки на соответствующее количество строк.
   */
  const applyFill = useCallback(
    (sourceSel: Sel, targetR: number) => {
      if (!editable) return;
      if (targetR === sourceSel.r2 && targetR >= sourceSel.r1) return;

      const patches: Rec[] = [];
      const sourceRowCount = sourceSel.r2 - sourceSel.r1 + 1;

      if (targetR > sourceSel.r2) {
        // Протягивание ВНИЗ
        for (let r = sourceSel.r2 + 1; r <= targetR; r++) {
          const targetRec = records[r];
          if (!targetRec) continue;

          const offsetInSource = (r - (sourceSel.r2 + 1)) % sourceRowCount;
          const srcR = sourceSel.r1 + offsetInSource;
          const sourceRec = records[srcR];
          if (!sourceRec) continue;

          const dr = r - srcR;
          const fields: Record<string, unknown> = {};

          for (let c = sourceSel.c1; c <= sourceSel.c2; c++) {
            const col = visibleCols[c]?.col;
            if (!col) continue;

            const val = sourceRec.fields[col.fields.colId];
            if (isFormula(val)) {
              fields[col.fields.colId] = shiftFormulaReferences(val, dr, 0);
            } else {
              fields[col.fields.colId] = val;
            }
          }

          if (Object.keys(fields).length) {
            patches.push({ id: targetRec.id, fields });
          }
        }

        setAnchor({ r: sourceSel.r1, c: sourceSel.c1 });
        setHead({ r: targetR, c: sourceSel.c2 });
      } else if (targetR < sourceSel.r1) {
        // Протягивание ВВЕРХ
        for (let r = targetR; r < sourceSel.r1; r++) {
          const targetRec = records[r];
          if (!targetRec) continue;

          const offsetInSource = (sourceSel.r1 - 1 - r) % sourceRowCount;
          const srcR = sourceSel.r2 - offsetInSource;
          const sourceRec = records[srcR];
          if (!sourceRec) continue;

          const dr = r - srcR;
          const fields: Record<string, unknown> = {};

          for (let c = sourceSel.c1; c <= sourceSel.c2; c++) {
            const col = visibleCols[c]?.col;
            if (!col) continue;

            const val = sourceRec.fields[col.fields.colId];
            if (isFormula(val)) {
              fields[col.fields.colId] = shiftFormulaReferences(val, dr, 0);
            } else {
              fields[col.fields.colId] = val;
            }
          }

          if (Object.keys(fields).length) {
            patches.push({ id: targetRec.id, fields });
          }
        }

        setAnchor({ r: sourceSel.r2, c: sourceSel.c2 });
        setHead({ r: targetR, c: sourceSel.c1 });
      }

      if (patches.length) {
        onPatch(patches);
      }
    },
    [editable, records, visibleCols, onPatch],
  );

  /** Размножает верхнюю строку выделения вниз по колонкам (с сохранением и смещением формул). */
  const fillDown = useCallback(() => {
    if (!editable) return;
    if (sel.r1 === sel.r2) return;
    applyFill({ ...sel, r2: sel.r1 }, sel.r2);
  }, [editable, sel, applyFill]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing) return;
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;

      const mod = e.ctrlKey || e.metaKey;

      const isKey = (code: string, ...keys: string[]) =>
        e.code === code || keys.some((k) => e.key.toLowerCase() === k.toLowerCase());

      if (mod && !e.shiftKey && isKey('KeyC', 'c', 'с')) {
        e.preventDefault();
        return copySelection();
      }
      if (mod && !e.shiftKey && isKey('KeyX', 'x', 'ч')) {
        e.preventDefault();
        return cut();
      }
      if (mod && !e.shiftKey && isKey('KeyD', 'd', 'в')) {
        e.preventDefault();
        return fillDown();
      }
      if (mod && !e.shiftKey && isKey('KeyV', 'v', 'м')) {
        e.preventDefault();
        navigator.clipboard.readText().then(paste);
        return;
      }
      if (mod && !e.shiftKey && isKey('KeyA', 'a', 'ф')) {
        e.preventDefault();
        setAnchor({ r: 0, c: 0 });
        setHead({ r: records.length - 1, c: visibleCols.length - 1 });
        return;
      }

      if (e.key === ' ' || (e.key === 'Enter' && visibleCols[head.c]?.col.fields.type === 'Bool')) {
        const targetCol = visibleCols[head.c]?.col;
        if (targetCol && targetCol.fields.type === 'Bool' && editable && !targetCol.fields.isFormula) {
          e.preventDefault();
          const firstVal = records[sel.r1]?.fields[targetCol.fields.colId];
          const nextVal = !isCheckboxChecked(firstVal);
          const patches: Rec[] = [];
          for (let r = sel.r1; r <= sel.r2; r++) {
            const rec = records[r];
            if (rec && !isFormula(rec.fields[targetCol.fields.colId])) {
              patches.push({ id: rec.id, fields: { [targetCol.fields.colId]: nextVal } });
            }
          }
          if (patches.length) onPatch(patches);
          return;
        }
      }

      switch (e.key) {
        case 'ArrowUp': e.preventDefault(); return move(-1, 0, e.shiftKey);
        case 'ArrowDown': e.preventDefault(); return move(1, 0, e.shiftKey);
        case 'ArrowLeft': e.preventDefault(); return move(0, -1, e.shiftKey);
        case 'ArrowRight': e.preventDefault(); return move(0, 1, e.shiftKey);
        case 'Tab': e.preventDefault(); return move(0, e.shiftKey ? -1 : 1, false);
        case 'Enter': e.preventDefault(); return startEdit(head);
        case 'Escape': return setCopied(null);
        case 'Delete':
        case 'Backspace': e.preventDefault(); return clear();
        default:
          if (e.key.length === 1 && !mod) startEdit(head, e.key);
      }
    };

    const onCopy = (e: ClipboardEvent) => {
      if (editing) return;
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;

      e.preventDefault();
      copySelection(e.clipboardData);
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('copy', onCopy);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('copy', onCopy);
    };
  }, [editing, head, sel, move, startEdit, copySelection, paste, clear, cut, fillDown,
      records, visibleCols, editable, onPatch]);

  const startColumnResize = useCallback((e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = widthOf(colId);
    let width = startWidth;

    const onMove = (ev: MouseEvent) => {
      width = Math.max(60, Math.round(startWidth + ev.clientX - startX));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (width !== startWidth) onResizeColumn?.(colId, width);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [widthOf, onResizeColumn]);

  const startRowResize = useCallback((e: React.MouseEvent, recordId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const startY = e.clientY;
    const startHeight = heightOf(recordId);
    let height = startHeight;

    const onMove = (ev: MouseEvent) => {
      height = Math.max(22, Math.round(startHeight + ev.clientY - startY));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (height !== startHeight) onResizeRow?.(recordId, height);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [heightOf, onResizeRow]);

  /** Меню по правому клику: своё для ячейки, строки и заголовка колонки. */
  const cellMenu = (r: number, c: number): MenuEntry[] => {
    const rec = records[r];
    const col = visibleCols[c]?.col;
    if (!rec || !col) return [];
    const origIdx = visibleCols[c]?.originalIndex ?? c;
    const multi = sel.r1 !== sel.r2 || sel.c1 !== sel.c2;
    const selectedRows = records.slice(sel.r1, sel.r2 + 1).filter(Boolean);

    return [
      {
        type: 'header',
        label: multi
          ? `Выделено ${colLabel(visibleCols[sel.c1]?.originalIndex ?? sel.c1)}${sel.r1 + 1}:${colLabel(visibleCols[sel.c2]?.originalIndex ?? sel.c2)}${sel.r2 + 1}`
          : `${col.fields.label} · ${colLabel(origIdx)}${r + 1}`,
      },
      { label: 'Копировать', icon: Copy, shortcut: 'Ctrl+C', onClick: copySelection },
      { label: 'Копировать (через | )', icon: Copy, onClick: copySelectionPiped },
      { label: 'Вырезать', icon: Scissors, shortcut: 'Ctrl+X', disabled: !editable, onClick: cut },
      {
        label: 'Вставить',
        icon: ClipboardPaste,
        shortcut: 'Ctrl+V',
        disabled: !editable,
        onClick: () => navigator.clipboard.readText().then(paste),
      },
      { type: 'divider' },
      {
        label: 'Редактировать значение',
        icon: Pencil,
        disabled: !editable,
        onClick: () => startEdit({ r, c }),
      },
      {
        label: 'Очистить',
        icon: Eraser,
        disabled: !editable,
        onClick: clear,
      },
      {
        label: 'Протянуть вниз',
        icon: ArrowDownWideNarrow,
        disabled: !editable || sel.r1 === sel.r2,
        onClick: () => fillDown(),
      },
      { type: 'divider' },
      {
        label: 'Добавить строку ниже',
        icon: Plus,
        disabled: !editable || !onAddRow,
        onClick: () => onAddRow?.(1, undefined),
      },
      {
        label:
          selectedRows.length > 1
            ? `Удалить строки (${selectedRows.length})`
            : 'Удалить выбранную строку',
        icon: Trash2,
        danger: true,
        disabled: !editable || !onDeleteRows,
        onClick: () => onDeleteRows?.(selectedRows.map((x) => x.id)),
      },
    ];
  };

  const rowMenu = (r: number): MenuEntry[] => {
    const rec = records[r];
    // Если кликнули по строке внутри выделения, команды работают по всему выделению.
    const inside = r >= sel.r1 && r <= sel.r2;
    const target = inside ? records.slice(sel.r1, sel.r2 + 1).filter(Boolean) : [rec];

    return [
      {
        type: 'header',
        label: target.length > 1 ? `Строк выделено: ${target.length}` : `Строка #${rec.id}`,
      },
      { label: 'Копировать', icon: Copy, shortcut: 'Ctrl+C', onClick: copySelection },
      { label: 'Копировать (через | )', icon: Copy, onClick: copySelectionPiped },
      { label: 'Вырезать', icon: Scissors, shortcut: 'Ctrl+X', disabled: !editable, onClick: cut },
      {
        label: 'Вставить',
        icon: ClipboardPaste,
        shortcut: 'Ctrl+V',
        disabled: !editable,
        onClick: () => navigator.clipboard.readText().then(paste),
      },
      { type: 'divider' },
      {
        label: 'Добавить строку ниже',
        icon: Plus,
        disabled: !editable || !onAddRow,
        onClick: () => onAddRow?.(1, rec.id),
      },
      { type: 'divider' },
      { label: 'История изменений', icon: History, onClick: () => onOpenHistory?.(rec.id) },
      {
        label: 'Автовысота',
        icon: ArrowRightLeft,
        disabled: !onAutoHeight,
        onClick: () => onAutoHeight?.(rec.id),
      },
      { type: 'divider' },
      {
        label: target.length > 1 ? `Дублировать строки (${target.length})` : 'Дублировать строку',
        icon: Copy,
        disabled: !editable || !onCreate,
        onClick: () => onCreate?.(target.map((t) => ({ fields: { ...t.fields } }))),
      },
      {
        label: target.length > 1 ? `Удалить строки (${target.length})` : 'Удалить строку',
        icon: Trash2,
        danger: true,
        disabled: !editable || !onDeleteRows,
        onClick: () => onDeleteRows?.(target.map((x) => x.id)),
      },
    ];
  };

  const columnMenu = (col: Column): MenuEntry[] => {
    const colIndex = visibleCols.findIndex((x) => x.col.fields.colId === col.fields.colId);
    const canMoveLeft = colIndex > 0;
    const canMoveRight = colIndex >= 0 && colIndex < visibleCols.length - 1;

    return [
      { type: 'header', label: `${col.fields.label} (${col.fields.colId})` },
      {
        label: 'Настройки колонки…',
        icon: SlidersHorizontal,
        disabled: !editable || !onOpenColumn,
        onClick: () => onOpenColumn?.(col),
      },
      {
        label: 'Тип колонки',
        icon: Type,
        disabled: !editable || !onSetColumnType,
        submenu: COLUMN_TYPES.map((type) => ({
          label: `${COLUMN_TYPE_LABELS[type]}${col.fields.type === type ? ' ✓' : ''}`,
          disabled: col.fields.type === type,
          onClick: () => onSetColumnType?.(col, type),
        })),
      },
      {
        label: 'Переименовать',
        icon: Pencil,
        disabled: !editable || !onRenameColumn,
        onClick: () => onRenameColumn?.(col),
      },
      { type: 'divider' },
      {
        label: 'Переместить влево',
        icon: ArrowLeft,
        disabled: !editable || !canMoveLeft || !onMoveColumn,
        onClick: () => onMoveColumn?.(col, 'left'),
      },
      {
        label: 'Переместить вправо',
        icon: ArrowRight,
        disabled: !editable || !canMoveRight || !onMoveColumn,
        onClick: () => onMoveColumn?.(col, 'right'),
      },
      {
        label: 'Вставить колонку слева',
        icon: Plus,
        disabled: !editable || !onInsertColumnAt,
        onClick: () => onInsertColumnAt?.(colIndex),
      },
      {
        label: 'Вставить колонку справа',
        icon: Plus,
        disabled: !editable || !onInsertColumnAt,
        onClick: () => onInsertColumnAt?.(colIndex + 1),
      },
      { type: 'divider' },
      {
        label: 'Скрыть колонку',
        icon: EyeOff,
        disabled: visibleCols.length <= 1,
        onClick: () => onToggleHideColumn?.(col.fields.colId),
      },
      ...(hiddenColIds && hiddenColIds.size > 0
        ? [
            {
              label: `Показать все скрытые колонки (${hiddenColIds.size})`,
              icon: Eye,
              onClick: () => onUnhideAllColumns?.(),
            },
          ]
        : []),
      {
        label: 'Автоширина',
        icon: ArrowRightLeft,
        disabled: !onAutoWidth,
        onClick: () => onAutoWidth?.(col.fields.colId),
      },
      { type: 'divider' },
      {
        label: 'Удалить колонку',
        icon: Trash2,
        danger: true,
        disabled: !editable || !onDeleteColumn,
        onClick: () => onDeleteColumn?.(col),
      },
    ];
  };

  const handleCellMouseDown = useCallback((e: React.MouseEvent, r: number, c: number) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') return;
    e.preventDefault();
    wrapRef.current?.focus();
    dragging.current = true;
    setAnchor({ r, c });
    setHead({ r, c });
  }, []);

  const handleCellMouseEnter = useCallback((r: number, c: number) => {
    if (!dragging.current) return;
    pendingHead.current = { r, c };
    if (!rafHeadId.current) {
      rafHeadId.current = requestAnimationFrame(() => {
        rafHeadId.current = null;
        if (pendingHead.current) {
          const next = pendingHead.current;
          pendingHead.current = null;
          setHead((prev) => {
            if (prev.r === next.r && prev.c === next.c) return prev;
            return next;
          });
        }
      });
    }
  }, []);

  const handleCellDoubleClick = useCallback(
    (r: number, c: number) => {
      const rec = records[r];
      const col = visibleCols[c]?.col;
      if (!rec || !col) return;

      if (!editable) {
        const cached = cellCache.get(`${rec.id}:${col.fields.colId}`);
        const isIntCol = col.fields.type === 'Int' || /преми/i.test(col.fields.label || '');
        let text = String(cached?.shown ?? rec.fields[col.fields.colId] ?? '');
        if (isIntCol) {
          const calc = isFormula(rec.fields[col.fields.colId])
            ? computed?.get(`${rec.id}:${col.fields.colId}`)
            : undefined;
          const val = calc ? calc.value : rec.fields[col.fields.colId];
          const n = Number(typeof val === 'string' ? val.replace(/\s+/g, '').replace(',', '.') : val);
          if (Number.isFinite(n)) {
            text = String(Math.round(n));
          }
        }
        if (text) {
          if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => {});
          }
          setCopied({ r1: r, r2: r, c1: c, c2: c });
          showCopyToast(text);
          setTimeout(() => {
            setCopied((prev) => (prev && prev.r1 === r && prev.c1 === c ? null : prev));
          }, 2500);
        }
        return;
      }
      if (col.fields.type === 'Bool') {
        if (!col.fields.isFormula && !isFormula(rec.fields[col.fields.colId])) {
          toggleCheckbox(rec, col);
        }
        return;
      }

      startEdit({ r, c });
    },
    [startEdit, editable, records, visibleCols, cellCache, showCopyToast, toggleCheckbox, computed],
  );

  const handleRowContextMenu = useCallback(
    (e: React.MouseEvent, r: number) => {
      if (r < sel.r1 || r > sel.r2) {
        setAnchor({ r, c: 0 });
        setHead({ r, c: Math.max(0, visibleCols.length - 1) });
      }
      menu.open(e, rowMenu(r));
    },
    [sel, visibleCols.length, menu, rowMenu],
  );

  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent, r: number, c: number) => {
      if (!inSel(sel, r, c)) {
        setAnchor({ r, c });
        setHead({ r, c });
      }
      menu.open(e, cellMenu(r, c));
    },
    [sel, menu, cellMenu],
  );

  /** Уголок выделения: тянем вниз/вверх мышью, двойной клик — до конца колонки. */
  const startFillDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const sourceSel = { ...sel };
      fillSourceRef.current = sourceSel;
      fillTargetRowRef.current = sourceSel.r2;
      setFillRange(sourceSel);

      document.body.style.cursor = 'crosshair';
      document.body.style.userSelect = 'none';

      const onMove = (ev: MouseEvent) => {
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const cell = el?.closest('td[data-row]');
        let row = cell ? Number((cell as HTMLElement).dataset.row) : NaN;

        if (Number.isNaN(row) && wrapRef.current) {
          const rect = wrapRef.current.getBoundingClientRect();
          if (ev.clientY > rect.bottom) {
            row = records.length - 1;
          } else if (ev.clientY < rect.top) {
            row = 0;
          }
        }

        if (!Number.isNaN(row) && row >= 0 && row < records.length) {
          fillTargetRowRef.current = row;
          const newRange: Sel = {
            r1: Math.min(sourceSel.r1, row),
            r2: Math.max(sourceSel.r2, row),
            c1: sourceSel.c1,
            c2: sourceSel.c2,
          };
          setFillRange(newRange);
        }
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        const targetRow = fillTargetRowRef.current;
        const source = fillSourceRef.current;
        fillTargetRowRef.current = null;
        fillSourceRef.current = null;
        setFillRange(null);

        if (source && targetRow !== null && targetRow !== source.r2) {
          applyFill(source, targetRow);
        }
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [sel, records.length, applyFill],
  );

  const fillToBottom = useCallback(() => {
    if (!editable || records.length <= 1) return;
    const checkCol = sel.c1 > 0 ? columns[sel.c1 - 1] : columns[sel.c2 + 1];
    let targetRow = records.length - 1;

    if (checkCol) {
      for (let r = sel.r2 + 1; r < records.length; r++) {
        const val = records[r]?.fields[checkCol.fields.colId];
        if (val === null || val === undefined || String(val).trim() === '') {
          targetRow = Math.max(sel.r2, r - 1);
          break;
        }
      }
    }

    if (targetRow > sel.r2) {
      applyFill(sel, targetRow);
    }
  }, [editable, records, columns, sel, applyFill]);

  if (!columns.length) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: R.textMuted, fontSize: 13 }}>
        В таблице пока нет колонок.
      </div>
    );
  }

  const totalWidth = GUTTER_W + visibleCols.reduce((sum, { col }) => sum + widthOf(col.fields.colId), 0);

  return (
    <div
      ref={wrapRef}
      tabIndex={0}
      className="rt-scroll"
      style={{
        flex: 1,
        overflow: 'auto',
        background: R.bg,
        willChange: 'scroll-position',
        contain: 'layout paint',
        outline: 'none',
      }}
    >
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: 13,
          tableLayout: 'fixed',
          width: totalWidth,
          userSelect: 'none',
        }}
      >
        <colgroup>
          <col style={{ width: GUTTER_W }} />
          {visibleCols.map(({ col }) => (
            <col key={col.id} style={{ width: widthOf(col.fields.colId) }} />
          ))}
        </colgroup>

        <thead>
          <tr>
            <th style={thCorner} />
            {visibleCols.map(({ col, originalIndex }, c) => {
              const highlighted = sel.c1 <= c && c <= sel.c2;
              const prevOrig = c === 0 ? -1 : visibleCols[c - 1].originalIndex;
              const hasHiddenBefore = originalIndex - prevOrig > 1;
              const hiddenBeforeCols = hasHiddenBefore
                ? columns.slice(prevOrig + 1, originalIndex)
                : [];

              const isLastVisible = c === visibleCols.length - 1;
              const hasHiddenAfter = isLastVisible && originalIndex < columns.length - 1;
              const hiddenAfterCols = hasHiddenAfter
                ? columns.slice(originalIndex + 1)
                : [];

              return (
                <th
                  key={col.id}
                  draggable={editable}
                  onDragStart={(e) => handleDragStart(e, col.fields.colId)}
                  onDragOver={(e) => handleDragOver(e, col.fields.colId)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, col.fields.colId)}
                  onDragEnd={handleDragEnd}
                  style={{
                    ...thCell,
                    background: highlighted ? R.accentSubtle : R.bgSubtle,
                    color: highlighted ? R.accent : R.textMuted,
                    borderBottom: `2px solid ${highlighted ? R.accent : R.border}`,
                    position: 'relative',
                    cursor: editable ? (dragColId ? 'grabbing' : 'grab') : 'pointer',
                    opacity: dragColId === col.fields.colId ? 0.4 : 1,
                    boxShadow:
                      dropTarget?.colId === col.fields.colId
                        ? dropTarget.side === 'left'
                          ? `inset 4px 0 0 ${R.accent}`
                          : `inset -4px 0 0 ${R.accent}`
                        : undefined,
                    transition: 'opacity 0.15s, box-shadow 0.15s',
                  }}
                  title={`${col.fields.colId} · ${col.fields.type}`}
                  onMouseDown={(e) => {
                    // Правый клик не трогает выделение — иначе оно схлопнется
                    // до этой колонки ещё до открытия меню.
                    if (e.button !== 0) return;
                    setAnchor({ r: 0, c });
                    setHead({ r: Math.max(0, records.length - 1), c });
                  }}
                  onDoubleClick={() => onRenameColumn?.(col)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    menu.open(e, columnMenu(col));
                  }}
                >
                  {hasHiddenBefore && onUnhideColumns && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnhideColumns(hiddenBeforeCols.map((x) => x.fields.colId));
                      }}
                      title={`Показать скрытые колонки (${hiddenBeforeCols.length}): ${hiddenBeforeCols.map((x, idx) => colLabel(prevOrig + 1 + idx)).join(', ')}`}
                      style={{
                        position: 'absolute',
                        left: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 20,
                        width: 16,
                        height: 20,
                        padding: 0,
                        background: R.accent,
                        border: '1.5px solid #ffffff',
                        borderRadius: 3,
                        color: '#ffffff',
                        fontSize: 9,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      }}
                    >
                      ↔
                    </button>
                  )}

                  {hasHiddenAfter && onUnhideColumns && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnhideColumns(hiddenAfterCols.map((x) => x.fields.colId));
                      }}
                      title={`Показать скрытые колонки (${hiddenAfterCols.length}): ${hiddenAfterCols.map((x, idx) => colLabel(originalIndex + 1 + idx)).join(', ')}`}
                      style={{
                        position: 'absolute',
                        right: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 20,
                        width: 16,
                        height: 20,
                        padding: 0,
                        background: R.accent,
                        border: '1.5px solid #ffffff',
                        borderRadius: 3,
                        color: '#ffffff',
                        fontSize: 9,
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                      }}
                    >
                      ↔
                    </button>
                  )}

                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', width: '100%', cursor: 'inherit' }}
                  >
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchor({ r: 0, c });
                        setHead({ r: Math.max(0, records.length - 1), c });
                      }}
                      style={{
                        fontSize: 10,
                        color: highlighted ? R.accent : R.textMuted,
                        fontWeight: 700,
                        lineHeight: 1,
                        cursor: 'pointer',
                      }}
                      title="Выделить столбец"
                    >
                      {colLabel(originalIndex)}
                    </span>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        menu.openAt(rect.left, rect.bottom + 2, columnMenu(col));
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        menu.open(e, columnMenu(col));
                      }}
                      title="Нажмите, чтобы открыть меню колонки"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        cursor: 'pointer',
                        borderRadius: 3,
                      }}
                    >
                      <ColumnTypeIcon type={col.fields.type} />
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          textTransform: 'none',
                          letterSpacing: 0,
                          fontSize: 12,
                          fontWeight: 500,
                          color: highlighted ? R.accent : R.text,
                        }}
                      >
                        {col.fields.label}
                      </span>

                      {onSort && onFilter && valuesFor && (
                        <span
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginLeft: 'auto', display: 'flex' }}
                        >
                          <ColumnFilter
                            colId={col.fields.colId}
                            title={col.fields.label}
                            values={valuesFor(col.fields.colId)}
                            sort={sort ?? null}
                            filter={filters?.get(col.fields.colId) ?? null}
                            onSort={onSort}
                            onFilter={(next) => onFilter(col.fields.colId, next)}
                          />
                        </span>
                      )}
                    </span>
                  </div>

                  {onResizeColumn && (
                    <span
                      onMouseDown={(e) => startColumnResize(e, col.fields.colId)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 5,
                        height: '100%',
                        cursor: 'col-resize',
                      }}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {slice.padTop > 0 && (
            <tr aria-hidden style={{ height: slice.padTop }}>
              <td colSpan={visibleCols.length + 1} style={{ padding: 0, border: 'none' }} />
            </tr>
          )}

          {records.slice(slice.from, slice.to).map((rec, i) => {
            const r = slice.from + i;
            const isRowSelected = sel.r1 <= r && r <= sel.r2;
            const selectedCols = isRowSelected ? { c1: sel.c1, c2: sel.c2 } : null;
            const isCopiedRow = copied ? copied.r1 <= r && r <= copied.r2 : false;
            const copiedCols = isCopiedRow && copied ? { c1: copied.c1, c2: copied.c2 } : null;
            const isFillRow = fillRange ? fillRange.r1 <= r && r <= fillRange.r2 : false;
            const fillCols = isFillRow && fillRange ? { c1: fillRange.c1, c2: fillRange.c2 } : null;
            const editingCol = editing?.r === r ? editing.c : null;
            const editingInitial = editing?.r === r ? editing.initial : null;
            const isBottomRightRow = r === sel.r2;
            const bottomRightCol = sel.c2;

            return (
              <GridRow
                key={rec.id}
                rec={rec}
                r={r}
                height={heightOf(rec.id)}
                columns={visibleColumns}
                origins={origins}
                skipped={skipped}
                cellCache={cellCache}
                isRowSelected={isRowSelected}
                selectedCols={selectedCols}
                isCopiedRow={isCopiedRow}
                copiedCols={copiedCols}
                isFillRow={isFillRow}
                fillCols={fillCols}
                editingCol={editingCol}
                editingInitial={editingInitial}
                editable={editable}
                isBottomRightRow={isBottomRightRow}
                bottomRightCol={bottomRightCol}
                onOpenHistory={onOpenHistory}
                onRowContextMenu={handleRowContextMenu}
                onResizeRow={onResizeRow ? startRowResize : undefined}
                onCellMouseDown={handleCellMouseDown}
                onCellMouseEnter={handleCellMouseEnter}
                onCellDoubleClick={handleCellDoubleClick}
                onCellContextMenu={handleCellContextMenu}
                onStartFillDrag={startFillDrag}
                onFillToBottom={fillToBottom}
                onCommitEdit={commitEdit}
                onCancelEdit={cancelEdit}
                onToggleCheckbox={toggleCheckbox}
              />
            );
          })}

          {slice.padBottom > 0 && (
            <tr aria-hidden style={{ height: slice.padBottom }}>
              <td colSpan={visibleCols.length + 1} style={{ padding: 0, border: 'none' }} />
            </tr>
          )}
        </tbody>
      </table>

      <menu.Portal />

      {records.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: R.textMuted, fontSize: 13 }}>
          Таблица пустая — добавьте строку
        </div>
      )}
    </div>
  );
}

interface GridRowProps {
  rec: Rec;
  r: number;
  height: number;
  columns: Column[];
  origins: Map<string, { rowspan: number; colspan: number }>;
  skipped: Set<string>;
  cellCache: Map<
    string,
    {
      shown: string;
      style: CellStyle;
      numeric: boolean;
      isError: boolean;
      title?: string;
      isBool?: boolean;
      boolChecked?: boolean;
    }
  >;
  isRowSelected: boolean;
  selectedCols: { c1: number; c2: number } | null;
  isCopiedRow: boolean;
  copiedCols: { c1: number; c2: number } | null;
  isFillRow: boolean;
  fillCols: { c1: number; c2: number } | null;
  editingCol: number | null;
  editingInitial: string | null;
  editable: boolean;
  isBottomRightRow: boolean;
  bottomRightCol: number | null;
  onOpenHistory?: (recordId: string) => void;
  onRowContextMenu: (e: React.MouseEvent, r: number) => void;
  onResizeRow?: (e: React.MouseEvent, recordId: string) => void;
  onCellMouseDown: (e: React.MouseEvent, r: number, c: number) => void;
  onCellMouseEnter: (r: number, c: number) => void;
  onCellDoubleClick: (r: number, c: number) => void;
  onCellContextMenu: (e: React.MouseEvent, r: number, c: number) => void;
  onStartFillDrag: (e: React.MouseEvent) => void;
  onFillToBottom: () => void;
  onCommitEdit: (value: string, moveDir?: { dr: number; dc: number }) => void;
  onCancelEdit: () => void;
  onToggleCheckbox?: (rec: Rec, col: Column) => void;
}

function areGridRowsEqual(prev: GridRowProps, next: GridRowProps): boolean {
  if (prev.rec !== next.rec) return false;
  if (prev.r !== next.r) return false;
  if (prev.height !== next.height) return false;
  if (prev.columns !== next.columns) return false;
  if (prev.isRowSelected !== next.isRowSelected) return false;
  if (prev.editingCol !== next.editingCol) return false;
  if (prev.editingInitial !== next.editingInitial) return false;
  if (prev.isCopiedRow !== next.isCopiedRow) return false;
  if (prev.isFillRow !== next.isFillRow) return false;
  if (prev.isBottomRightRow !== next.isBottomRightRow) return false;
  if (prev.bottomRightCol !== next.bottomRightCol) return false;
  if (prev.editable !== next.editable) return false;
  if (prev.cellCache !== next.cellCache) return false;
  if (prev.origins !== next.origins) return false;
  if (prev.skipped !== next.skipped) return false;
  if (prev.onToggleCheckbox !== next.onToggleCheckbox) return false;

  if (prev.selectedCols !== next.selectedCols) {
    if (!prev.selectedCols || !next.selectedCols) return false;
    if (
      prev.selectedCols.c1 !== next.selectedCols.c1 ||
      prev.selectedCols.c2 !== next.selectedCols.c2
    ) {
      return false;
    }
  }

  if (prev.copiedCols !== next.copiedCols) {
    if (!prev.copiedCols || !next.copiedCols) return false;
    if (
      prev.copiedCols.c1 !== next.copiedCols.c1 ||
      prev.copiedCols.c2 !== next.copiedCols.c2
    ) {
      return false;
    }
  }

  if (prev.fillCols !== next.fillCols) {
    if (!prev.fillCols || !next.fillCols) return false;
    if (
      prev.fillCols.c1 !== next.fillCols.c1 ||
      prev.fillCols.c2 !== next.fillCols.c2
    ) {
      return false;
    }
  }

  return true;
}

const GridRow = React.memo(function GridRow({
  rec,
  r,
  height,
  columns,
  origins,
  skipped,
  cellCache,
  isRowSelected,
  selectedCols,
  isCopiedRow,
  copiedCols,
  isFillRow,
  fillCols,
  editingCol,
  editingInitial,
  editable,
  isBottomRightRow,
  bottomRightCol,
  onOpenHistory,
  onRowContextMenu,
  onResizeRow,
  onCellMouseDown,
  onCellMouseEnter,
  onCellDoubleClick,
  onCellContextMenu,
  onStartFillDrag,
  onFillToBottom,
  onCommitEdit,
  onCancelEdit,
  onToggleCheckbox,
}: GridRowProps) {
  return (
    <tr key={rec.id} className="rt-grid-row" style={{ height }}>
      <td
        className="rt-gutter"
        style={{
          ...tdGutter,
          background: isRowSelected ? R.accentSubtle : R.bgSubtle,
          color: isRowSelected ? R.accent : R.textMuted,
        }}
        onDoubleClick={() => onOpenHistory?.(rec.id)}
        onContextMenu={(e) => onRowContextMenu(e, r)}
        title="Двойной клик — история строки, правый клик — меню"
      >
        {r + 1}
        {onResizeRow && (
          <span
            onMouseDown={(e) => onResizeRow(e, rec.id)}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 5,
              cursor: 'row-resize',
            }}
          />
        )}
      </td>

      {columns.map((col, c) => {
        if (skipped.has(`${r}:${c}`)) return null;
        const span = origins.get(`${r}:${c}`);

        const cached = cellCache.get(`${rec.id}:${col.fields.colId}`);
        const shown = cached?.shown ?? '';
        const style = cached?.style ?? {};
        const numeric = cached?.numeric ?? false;
        const title = cached?.title;

        const selected = selectedCols ? c >= selectedCols.c1 && c <= selectedCols.c2 : false;
        const isCopied = copiedCols ? c >= copiedCols.c1 && c <= copiedCols.c2 : false;
        const isFilled = fillCols ? c >= fillCols.c1 && c <= fillCols.c2 : false;
        const isEditing = editingCol === c;

        return (
          <td
            key={col.id}
            rowSpan={span?.rowspan}
            colSpan={span?.colspan}
            className={`rt-grid-cell ${selected ? 'rt-cell--selected' : ''}`}
            data-custom-bg={style.bg ? 'true' : undefined}
            data-row={r}
            title={title || (!editable ? 'Кликните для выбора ячейки (Ctrl+C — скопировать)' : undefined)}
            style={{
              ...tdCell,
              position: 'relative',
              verticalAlign: style.v ?? 'middle',
              background: style.bg || (selected ? R.selectionSubtle : (isFilled ? 'rgba(52, 152, 219, 0.08)' : R.bg)),
              boxShadow: selected && !isEditing ? `inset 0 0 0 1.5px ${R.accent}` : undefined,
              outline: isCopied ? `2px dashed ${R.accent}` : (isFilled && !selected ? `1.5px dashed ${R.accent}` : undefined),
              outlineOffset: -1,
              cursor: editable ? 'cell' : 'pointer',
            }}
            onMouseDown={(e) => onCellMouseDown(e, r, c)}
            onMouseEnter={() => onCellMouseEnter(r, c)}
            onDoubleClick={() => onCellDoubleClick(r, c)}
            onContextMenu={(e) => onCellContextMenu(e, r, c)}
          >
            {editable && isBottomRightRow && bottomRightCol === c && !isEditing && (
              <span
                onMouseDown={onStartFillDrag}
                onDoubleClick={onFillToBottom}
                title="Протянуть формулу/значение вниз (двойной клик — автозаполнение до конца)"
                style={{
                  position: 'absolute',
                  right: -4,
                  bottom: -4,
                  width: 8,
                  height: 8,
                  background: R.accent,
                  border: '1.5px solid #ffffff',
                  borderRadius: 1,
                  boxShadow: '0 0 2px rgba(0,0,0,0.4)',
                  cursor: 'crosshair',
                  zIndex: 10,
                }}
              />
            )}

            {isEditing ? (
              <CellEditor
                column={col}
                initialValue={editingInitial ?? ''}
                onCommit={onCommitEdit}
                onCancel={onCancelEdit}
                style={style}
              />
            ) : cached?.isBool ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent:
                    (style.h ?? 'center') === 'left'
                      ? 'flex-start'
                      : (style.h ?? 'center') === 'right'
                        ? 'flex-end'
                        : 'center',
                  height: '100%',
                  padding: '0 8px',
                }}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  disabled={!editable}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editable) {
                      onToggleCheckbox?.(rec, col);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onCellMouseDown(e, r, c);
                  }}
                  aria-label={cached.boolChecked ? 'Снять отметку' : 'Поставить отметку'}
                  title={editable ? 'Нажмите для изменения галочки' : undefined}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: cached.boolChecked ? `1.5px solid ${R.accent}` : `1.5px solid ${R.border}`,
                    backgroundColor: cached.boolChecked ? R.accent : 'transparent',
                    color: '#ffffff',
                    cursor: editable ? 'pointer' : 'default',
                    padding: 0,
                    margin: 0,
                    transition: 'all 0.15s ease',
                    outline: 'none',
                    boxShadow: cached.boolChecked ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {cached.boolChecked && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              </div>
            ) : (
              <span
                style={{
                  display: 'flex',
                  alignItems:
                    style.v === 'top'
                      ? 'flex-start'
                      : style.v === 'bottom'
                        ? 'flex-end'
                        : 'center',
                  justifyContent:
                    (style.h ?? (numeric ? 'right' : 'left')) === 'center'
                      ? 'center'
                      : (style.h ?? (numeric ? 'right' : 'left')) === 'right'
                        ? 'flex-end'
                        : 'flex-start',
                  height: '100%',
                  padding: '0 8px',
                  overflow: 'hidden',
                  textOverflow: style.w ? undefined : 'ellipsis',
                  whiteSpace: style.w ? 'normal' : 'nowrap',
                  wordBreak: style.w ? 'break-word' : undefined,
                  fontWeight: style.b ? 700 : undefined,
                  fontStyle: style.i ? 'italic' : undefined,
                  textDecoration:
                    [style.u ? 'underline' : '', style.s ? 'line-through' : '']
                      .filter(Boolean)
                      .join(' ') || undefined,
                  fontFamily: style.ff ? `${style.ff}, Archivo, sans-serif` : undefined,
                  fontSize: style.fs ? `${style.fs}px` : undefined,
                  color: cached?.isError ? R.danger : style.fg || R.text,
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                }}
              >
                {shown}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}, areGridRowsEqual);

function CellEditor({
  column,
  initialValue,
  onCommit,
  onCancel,
  style,
}: {
  column: Column;
  initialValue: string;
  onCommit: (v: string, moveDir?: { dr: number; dc: number }) => void;
  onCancel: () => void;
  style?: CellStyle;
}) {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLInputElement | HTMLSelectElement>(null);

  useEffect(() => {
    ref.current?.focus();
    if (ref.current instanceof HTMLInputElement) {
      ref.current.select();
    }
  }, []);

  const textAlign =
    style?.h ?? (column.fields.type === 'Numeric' || column.fields.type === 'Int' ? 'right' : 'left');

  const shared = {
    style: {
      width: '100%',
      height: '100%',
      padding: '0 8px',
      border: 'none',
      outline: 'none',
      boxShadow: 'none',
      background: 'transparent',
      color: style?.fg || R.text,
      fontSize: style?.fs ? `${style.fs}px` : 13,
      userSelect: 'text' as const,
      WebkitUserSelect: 'text' as const,
      fontFamily: value.trimStart().startsWith('=')
        ? mono
        : style?.ff
          ? `${style.ff}, Archivo, sans-serif`
          : undefined,
      fontWeight: style?.b ? 700 : undefined,
      fontStyle: style?.i ? 'italic' : undefined,
      textAlign: textAlign as React.CSSProperties['textAlign'],
      boxSizing: 'border-box' as const,
    } as React.CSSProperties,
    onBlur: () => onCommit(value),
    onKeyDown: (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        onCommit(value, e.shiftKey ? { dr: -1, dc: 0 } : { dr: 1, dc: 0 });
      } else if (e.key === 'Tab') {
        e.preventDefault();
        onCommit(value, e.shiftKey ? { dr: 0, dc: -1 } : { dr: 0, dc: 1 });
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
  };

  const choices = column.fields.widgetOptions?.choices;

  if (column.fields.type === 'Choice' && choices?.length && !value.trimStart().startsWith('=')) {
    return (
      <select
        {...shared}
        ref={ref as React.Ref<HTMLSelectElement>}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          onCommit(e.target.value);
        }}
      >
        <option value="">(пусто)</option>
        {choices.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  }

  // Пока в ячейке формула, поле должно быть текстовым — иначе «=» не ввести.
  const formula = value.trimStart().startsWith('=');

  const inputType =
    formula
      ? 'text'
      : column.fields.type === 'Date'
        ? 'date'
        : column.fields.type === 'DateTime'
          ? 'datetime-local'
          : column.fields.type === 'Numeric' || column.fields.type === 'Int'
            ? 'number'
            : 'text';

  return (
    <input
      {...shared}
      ref={ref as React.Ref<HTMLInputElement>}
      type={inputType}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}
